/**
 * Shared utility functions for relationship handling across entity types
 */

import { normalizeEntityType } from "./SectionConfig";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();

/**
 * Build a merged list of related entities from both database and form data
 * Filters by entity type and removes duplicates
 */
export const buildRelatedEntities = (
  editingEntity,
  entityForm,
  removedItems,
  relationships,
  entities,
  relationshipTypes,
  relatedEntityType,
  sectionKey,
  allEntities = {} // Add allEntities parameter
) => {
  // For new entities (not editing), only return form data
  if (!editingEntity) {
    const relatedFromForm = entityForm[sectionKey] || [];
    const removedIds = removedItems[sectionKey] || [];
    return relatedFromForm.filter(
      (formItem) => !removedIds.includes(formItem.id)
    );
  }

  // For existing entities, merge DB and form data
  // Filter relationships by dynamic relationship types from backend (or server rules/hardcoded mapping)
  console.log(
    `🔍 [buildRelatedEntities] Filtering for sectionKey: ${sectionKey}, relatedEntityType: ${relatedEntityType}`,
    {
      editingEntityId: editingEntity?.id,
      relationshipsCount: relationships?.length || 0,
      relationshipTypes: relationshipTypes,
      relationshipTypesLength: relationshipTypes.length,
      entitiesCount: entities?.length || 0,
    }
  );

  let logCount = 0;
  const rels = relationships.filter((rel) => {
    const isRelated =
      rel.source_entity_id === editingEntity.id ||
      rel.target_entity_id === editingEntity.id;
    if (!isRelated) return false;

    // If no relationship type filter specified, allow all
    if (relationshipTypes.length === 0) {
      if (logCount < 5) {
        console.log(
          `🔍 [buildRelatedEntities] No relationship type filter, allowing: ${rel.type}`
        );
        logCount++;
      }
      return true;
    }

    // Case-insensitive matching
    const relTypeLower = String(rel.type || "")
      .toLowerCase()
      .trim();
    const matches = relationshipTypes.includes(relTypeLower);

    if (logCount < 5) {
      // Only log first few to avoid spam
      console.log(`🔍 [buildRelatedEntities] Checking relationship:`, {
        relType: rel.type,
        relTypeLower: relTypeLower,
        allowedTypes: relationshipTypes,
        matches: matches,
        source: rel.source_entity_id,
        target: rel.target_entity_id,
      });
      logCount++;
    }

    return matches;
  });

  console.log(
    `🔍 [buildRelatedEntities] Filtered relationships: ${rels.length} out of ${relationships?.length || 0}`
  );

  let processLogCount = 0;
  let entityNotFoundLogCount = 0;
  let typeMismatchLogCount = 0;
  let typeMatchLogCount = 0;

  const relatedFromDB = rels
    .map((rel) => {
      const isOutgoing = rel.source_entity_id === editingEntity.id;
      const entityId = isOutgoing ? rel.target_entity_id : rel.source_entity_id;

      if (processLogCount < 3) {
        console.log(`🔍 [buildRelatedEntities] Processing relationship:`, {
          relId: rel.id,
          relType: rel.type,
          isOutgoing: isOutgoing,
          sourceEntityId: rel.source_entity_id,
          targetEntityId: rel.target_entity_id,
          editingEntityId: editingEntity.id,
          lookingForEntityId: entityId,
          sourceEntityType: rel.source_entity_type,
          targetEntityType: rel.target_entity_type,
          relatedEntityType: relatedEntityType,
        });
        processLogCount++;
      }

      // First try entities array, then allEntities map
      let entity = entities.find((e) => e.id === entityId);

      if (!entity && allEntities[entityId]) {
        // Use allEntities as fallback
        const entityData = allEntities[entityId];
        entity = {
          id: entityData.id || entityId,
          name: entityData.name,
          type: entityData.type || entityData.entity_type,
          entity_type: entityData.entity_type || entityData.type,
          description: entityData.description,
        };
      }

      // Get relationship's entity type fields as they might be more accurate
      const relEntityType = isOutgoing
        ? rel.target_entity_type
        : rel.source_entity_type;

      // If entity is missing or has no type, try to use relationship's entity type
      if (!entity) {
        // If we have type info from relationship, create a minimal entity object
        if (relEntityType) {
          entity = {
            id: entityId,
            name: `Entity ${entityId.substring(0, 8)}...`, // Placeholder name
            type: relEntityType,
            entity_type: relEntityType,
          };
        } else {
          if (entityNotFoundLogCount < 3) {
            console.log(`🔍 [buildRelatedEntities] Entity not found:`, {
              entityId: entityId,
              relatedEntityType: relatedEntityType,
              entitiesCount: entities.length,
              allEntitiesCount: Object.keys(allEntities || {}).length,
              inEntitiesArray: entities.some((e) => e.id === entityId),
              inAllEntities: !!allEntities[entityId],
              relEntityType: relEntityType,
            });
            entityNotFoundLogCount++;
          }
          return null;
        }
      } else {
        // If entity exists but has no type, try to use relationship's entity type as fallback
        const entityType = entity.type || entity.entity_type;
        if (!entityType && relEntityType) {
          entity = {
            ...entity,
            type: relEntityType,
            entity_type: relEntityType,
          };
        }
      }

      // Check entity type match
      // Also check relationship's entity type fields as they might be more accurate
      const entityType = entity.type || entity.entity_type;
      const normalizedEntityType = normalizeEntityType(entityType);
      const normalizedRelatedType = normalizeEntityType(relatedEntityType);
      const normalizedRelType = relEntityType
        ? normalizeEntityType(relEntityType)
        : null;

      // Match if entity type matches OR relationship entity type matches
      const typeMatches =
        entityType === relatedEntityType ||
        normalizedEntityType === normalizedRelatedType ||
        (relEntityType &&
          (relEntityType === relatedEntityType ||
            normalizedRelType === normalizedRelatedType));

      if (!typeMatches) {
        if (typeMismatchLogCount < 3) {
          console.log(`🔍 [buildRelatedEntities] Entity type mismatch:`, {
            entityId: entity.id,
            entityName: entity.name,
            entityType: entityType,
            relEntityType: relEntityType,
            relatedEntityType: relatedEntityType,
            normalizedEntityType: normalizedEntityType,
            normalizedRelType: normalizedRelType,
            normalizedRelatedType: normalizedRelatedType,
            exactMatch: entityType === relatedEntityType,
            normalizedMatch: normalizedEntityType === normalizedRelatedType,
            relTypeMatch:
              relEntityType &&
              (relEntityType === relatedEntityType ||
                normalizedRelType === normalizedRelatedType),
          });
          typeMismatchLogCount++;
        }
        return null;
      }

      if (typeMatchLogCount < 3) {
        console.log(`✅ [buildRelatedEntities] Entity type matched:`, {
          entityId: entity.id,
          entityName: entity.name,
          entityType: entityType,
          relatedEntityType: relatedEntityType,
        });
        typeMatchLogCount++;
      }
      return {
        ...entity,
        activeFrom: rel.active_from,
        activeUntil: rel.active_until,
        description: rel.description,
        status: rel.status,
      };
    })
    .filter(Boolean);

  console.log(
    `🔍 [buildRelatedEntities] Found ${relatedFromDB.length} entities from DB for ${sectionKey}`
  );

  const relatedFromForm = entityForm[sectionKey] || [];
  const removedIds = removedItems[sectionKey] || [];
  const filteredDbItems = relatedFromDB.filter(
    (dbItem) => !removedIds.includes(dbItem.id)
  );

  const allRelated = [...filteredDbItems, ...relatedFromForm];
  const uniqueRelated = allRelated.filter(
    (item, index, self) => index === self.findIndex((t) => t.id === item.id)
  );

  return uniqueRelated;
};

/**
 * Get icon for a relationship type based on entity type and direction
 */
export const getRelationshipIcon = (
  relatedType,
  entityTypesMap,
  direction = "outgoing"
) => {
  // For interfaces, use direction-based icon
  if (
    relatedType?.toLowerCase() === "interface" ||
    relatedType?.toLowerCase().includes("interface")
  ) {
    return direction === "incoming" ? "📥" : "📤";
  }

  // First try the exact type name (database format: lowercase-with-hyphens)
  if (entityTypesMap[relatedType]) {
    return entityTypesMap[relatedType];
  }

  // Try PascalCase version (frontend format)
  if (entityTypesMap[relatedType?.toLowerCase()]) {
    return entityTypesMap[relatedType?.toLowerCase()];
  }

  // Try with underscores replaced with hyphens
  const hyphenated = relatedType?.replace(/_/g, "-");
  if (entityTypesMap[hyphenated]) {
    return entityTypesMap[hyphenated];
  }

  // Fallback to default
  return "🔗";
};

/**
 * Get human-readable label for relationship actions
 */
export const getHumanLabel = (
  sectionKey,
  relatedType,
  entityFormType,
  itemsLength
) => {
  if (sectionKey === "related_successors") {
    return entityFormType === "application"
      ? "Add Application"
      : entityFormType === "technology-component"
        ? "Add Technology Component"
        : "Add Application or Technology Component";
  }

  if (sectionKey.includes("_parents")) {
    return itemsLength > 0 ? "Replace Parent" : "Add Parent";
  }

  if (sectionKey === "interface_provided_by_app") {
    return itemsLength > 0 ? "Replace Provider" : "Add Application";
  }
  // interface_*_by_sc retired with Solution Component.

  return `Add ${relatedType.replace(/([A-Z])/g, " $1").trim()}`;
};

/**
 * Find relationships by type and direction
 */
export const findRelationshipsByType = (
  relationships,
  targetEntityId,
  types,
  isTarget = true
) => {
  return relationships.filter((rel) => {
    const typeLower = (rel.type || "").toLowerCase();
    const hasCorrectType = types.includes(typeLower);

    if (isTarget) {
      return hasCorrectType && rel.target_entity_id === targetEntityId;
    } else {
      return hasCorrectType && rel.source_entity_id === targetEntityId;
    }
  });
};

/**
 * Extract entities from relationships
 */
export const extractEntitiesFromRelationships = (
  relationships,
  entities,
  entityType,
  editingEntity,
  isTarget = true
) => {
  // Normalize entity type for comparison (case-insensitive)
  const normalizedEntityType = (entityType || "").toLowerCase();

  return relationships
    .map((rel) => {
      const otherEntityId = isTarget
        ? rel.source_entity_id
        : rel.target_entity_id;
      const entity = entities.find((e) => {
        if (e.id !== otherEntityId) return false;
        // Check both type and entity_type fields, case-insensitively
        const eType = (e.type || "").toLowerCase();
        const eEntityType = (e.entity_type || "").toLowerCase();
        return (
          eType === normalizedEntityType || eEntityType === normalizedEntityType
        );
      });

      if (!entity) return null;

      return {
        ...entity,
        activeFrom: rel.active_from,
        activeUntil: rel.active_until,
        description: rel.description,
        status: rel.status,
      };
    })
    .filter(Boolean);
};

/**
 * Merge database relationships with form data
 */
export const mergeRelationshipsWithForm = (
  dbRelationships,
  entityForm,
  removedItems,
  sectionKey
) => {
  const relatedFromForm = entityForm[sectionKey] || [];
  const removedIds = removedItems[sectionKey] || [];
  const filteredDbItems = dbRelationships.filter(
    (dbItem) => !removedIds.includes(dbItem.id)
  );
  const allRelated = [...filteredDbItems, ...relatedFromForm];

  return allRelated.filter(
    (item, index, self) => index === self.findIndex((t) => t.id === item.id)
  );
};
